import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Add from '@material-ui/icons/Add';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import ViewListIcon from '@material-ui/icons/ViewList';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import ical from 'ical-generator';
import { useSelector } from 'react-redux';
import DashboardUpcomingList from './DashboardUpcomingList';
import DashboardUpcomingCalendar from './DashboardUpcomingCalendar';
import MyTasksStatusSummary from './MyTasksStatusSummary';
import MyTasksFilterDialog, { FilterOption } from './MyTasksFilterDialog';
import MyTasksBreakdownChart, { getBreakdownColorsByValue } from './MyTasksBreakdownChart';
import {
  getSoonestDueDocuments,
  getSoonestDueDocumentsAssignedByUser,
  getSoonestDueDocumentsAssignedToUser,
} from '../../models/api/documents';
import { DocumentTemplateType, INumberedDocumentView } from '../../api-client/autogenerated';
import { getProjectState } from '../../features/project/selectors';
import { getUserState } from '../../features/user/selectors';
import {
  computeStatusBucketCounts,
  documentIsOverdue,
  documentTypeToReadable,
  getStatusBucketLabel,
  STATUS_BUCKET_LABELS,
} from '../../scripts/utils';
import { useAppUtilsContext } from '../AppUtilsContext';
import getProjectsState from '../../features/projects/selectors';
import { filterDocumentsWithVisibleProject } from './myWorkVisibility';

const OVERDUE_FILTER_VALUE = 'overdue';

type TaskView = 'table' | 'calendar';

/**
 * The document type a consultant/additional-review document should be categorized and filtered
 * as: its parent document's type (e.g. a Submittal's consultant review counts as "Submittal"),
 * not its own "Consultant Review" template. Every other document uses its own type as usual.
 */
const getEffectiveDocumentType = (
  doc: INumberedDocumentView,
  documentTemplateIdToName: Record<string, DocumentTemplateType>,
) =>
  doc.additionalReviewForDocument
    ? documentTemplateIdToName[doc.additionalReviewForDocument.documentTemplateId]
    : documentTemplateIdToName[doc.documentTemplateId];

/** MyTasksPage's off-white page canvas — the surface the charts sit directly on there. */
const PAGE_BACKGROUND = '#FAFAFA';

interface MyTasksContentProps {
  /** Rows-per-page selector options — the first entry is the default. Defaults to 25/50/100. */
  rowsPerPageOptions?: number[];
  /** Surface color the two summary charts are drawn on. Defaults to the My Work page's #FAFAFA
   * canvas; the dashboard card passes white, since there they sit on a white CardContent. */
  chartBackgroundColor?: string;
}

/**
 * The My Work page's charts/tabs/filter/view-toggle and table/calendar content, shared by
 * the standalone My Work page (MyTasksPage, which wraps this in NavAppbar + PageTitle) and the
 * Dashboard's full-width "My Work" card (DashboardMyTasksCard, which wraps this in a Card) — one
 * implementation neither can fall out of sync with.
 */
export default function MyTasksContent({
  rowsPerPageOptions,
  chartBackgroundColor = PAGE_BACKGROUND,
}: MyTasksContentProps) {
  const user = useSelector(getUserState);
  const project = useSelector(getProjectState);

  const [upcomingType, setUpcomingType] = useState('assigned-to-me');
  const [view, setView] = useState<TaskView>('table');
  const [loading, setLoading] = useState(true);
  const [soonestDueDocuments, setSoonestDueDocuments] = useState<INumberedDocumentView[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [projectFilters, setProjectFilters] = useState<string[]>([]);
  const [docTypeFilters, setDocTypeFilters] = useState<string[]>([]);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const {
    documentTemplates: { documentTemplateIdToName },
  } = useAppUtilsContext();
  const projects = useSelector(getProjectsState);

  // Refs the reused calendar view needs for its print/export affordances.
  const calendarRef = useRef<any>(null);
  const iCalendar = useRef(ical({ name: 'Centerline Calendar' }));

  useEffect(() => {
    setLoading(true);
    const fetcher =
      upcomingType === 'assigned-by-me'
        ? getSoonestDueDocumentsAssignedByUser
        : upcomingType === 'all'
        ? getSoonestDueDocuments
        : getSoonestDueDocumentsAssignedToUser;
    fetcher(user.id, project?.id)
      .then(setSoonestDueDocuments)
      .catch((e) => console.log(e))
      .finally(() => setLoading(false));
  }, [upcomingType, project, user.id]);

  // The underlying document set changes with the tab/project, so stale filters would silently
  // hide everything rather than carry over meaningfully.
  useEffect(() => {
    setStatusFilters([]);
    setProjectFilters([]);
    setDocTypeFilters([]);
  }, [upcomingType, project?.id]);

  // The tab-scoped documents this page can actually present: the fetch reaches documents in
  // projects that aren't in the portal's project list (archived, hidden, or ones the user only
  // touches as a document's creator/assignee), and the table has always dropped those rows. Every
  // chart, facet, count and table page below is derived from THIS list rather than the raw fetch,
  // so they all describe the same documents — see myWorkVisibility for what used to go wrong.
  const visibleDocuments = useMemo(
    () => filterDocumentsWithVisibleProject(soonestDueDocuments, projects),
    [soonestDueDocuments, projects],
  );

  // Facet counts/options are derived from the unfiltered tab-scoped list, so option lists and
  // counts stay stable (what you could ALSO filter to) rather than shrinking to nothing once a
  // filter is applied.
  const { counts: statusCounts, overdueCount } = useMemo(
    () => computeStatusBucketCounts(visibleDocuments, documentTemplateIdToName),
    [visibleDocuments, documentTemplateIdToName],
  );

  const statusOptions: FilterOption[] = useMemo(
    () =>
      STATUS_BUCKET_LABELS.filter((b) => statusCounts[b.label] > 0).map((b) => ({
        value: b.label,
        label: b.label,
        count: statusCounts[b.label],
      })),
    [statusCounts],
  );

  const overdueOption: FilterOption | null =
    overdueCount > 0
      ? { value: OVERDUE_FILTER_VALUE, label: 'Overdue', count: overdueCount }
      : null;

  // visibleDocuments has already excluded anything whose project isn't presentable, so every
  // document here resolves to a project and no project appears with zero rows it could show.
  const projectOptions: FilterOption[] = useMemo(() => {
    const byProject = new Map<string, { name: string; count: number }>();
    visibleDocuments.forEach((doc) => {
      const proj = projects.find((p) => p.id === doc.projectId);
      if (!proj) return;
      const entry = byProject.get(proj.id) || { name: proj.name, count: 0 };
      entry.count += 1;
      byProject.set(proj.id, entry);
    });
    return Array.from(byProject.entries())
      .map(([id, { name, count }]) => ({ value: id, label: name, count }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleDocuments, projects]);

  // Colors the table's project pill exactly like that project's slice in the "By Project"
  // breakdown chart, keyed by project id for a quick per-row lookup.
  const projectColorsById = useMemo(() => getBreakdownColorsByValue(projectOptions), [
    projectOptions,
  ]);

  const docTypeOptions: FilterOption[] = useMemo(() => {
    const byType = new Map<string, number>();
    visibleDocuments.forEach((doc) => {
      const type = getEffectiveDocumentType(doc, documentTemplateIdToName);
      if (!type) return;
      byType.set(type, (byType.get(type) || 0) + 1);
    });
    return Array.from(byType.entries())
      .map(([type, count]) => ({
        value: type,
        label: documentTypeToReadable[type as keyof typeof documentTypeToReadable] ?? type,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [visibleDocuments, documentTemplateIdToName]);

  // Colors the table's document type pill exactly like that type's slice in the "By Type"
  // breakdown chart, keyed by document type for a quick per-row lookup. Only rendered as a pill
  // viewing a single project — see DashboardUpcomingList's docTypeColors prop.
  const docTypeColorsById = useMemo(() => getBreakdownColorsByValue(docTypeOptions), [
    docTypeOptions,
  ]);

  const filteredDocuments = useMemo(() => {
    if (statusFilters.length === 0 && projectFilters.length === 0 && docTypeFilters.length === 0) {
      return visibleDocuments;
    }
    return visibleDocuments.filter((doc) => {
      if (statusFilters.length > 0) {
        const documentType = documentTemplateIdToName[doc.documentTemplateId];
        const matchesOverdue =
          statusFilters.includes(OVERDUE_FILTER_VALUE) && documentIsOverdue(doc);
        const bucketLabel = getStatusBucketLabel(doc, documentType);
        const matchesBucket = !!bucketLabel && statusFilters.includes(bucketLabel);
        if (!matchesOverdue && !matchesBucket) return false;
      }
      if (projectFilters.length > 0 && !projectFilters.includes(doc.projectId)) return false;
      if (docTypeFilters.length > 0) {
        const effectiveType = getEffectiveDocumentType(doc, documentTemplateIdToName);
        if (!effectiveType || !docTypeFilters.includes(effectiveType)) return false;
      }
      return true;
    });
  }, [visibleDocuments, statusFilters, projectFilters, docTypeFilters, documentTemplateIdToName]);

  const activeFilterCount = statusFilters.length + projectFilters.length + docTypeFilters.length;

  // Clicking a status slice/legend item filters to exactly that status, replacing any other
  // status selection (e.g. picked via the filter dialog's checkboxes) — clicking the same slice
  // again clears it back to no status filter.
  const setStatusFilter = (value: string) => {
    setStatusFilters((prev) => (prev.length === 1 && prev[0] === value ? [] : [value]));
  };

  // Shared by the project/document-type breakdown charts: clicking a real slice filters to
  // exactly that one value; clicking the grouped "Other" slice filters to exactly the values it
  // represents together. Either replaces any existing selection for that dimension, and clicking
  // the same slice again clears it back to no filter.
  const setFilterValues = (
    current: string[],
    setCurrent: (v: string[]) => void,
    values: string[],
  ) => {
    const isSameSelection =
      current.length === values.length && values.every((v) => current.includes(v));
    setCurrent(isSameSelection ? [] : values);
  };

  const tabs: { value: string; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'assigned-to-me', label: 'Assigned to me' },
    { value: 'assigned-by-me', label: 'Assigned by me to others' },
  ];

  return (
    <>
      <Grid container spacing={1} style={{ marginTop: 16 }}>
        <Grid item xs={12} md={6}>
          <MyTasksStatusSummary
            documents={visibleDocuments}
            documentTemplateIdToName={documentTemplateIdToName}
            activeStatuses={statusFilters}
            activeOverdue={statusFilters.includes(OVERDUE_FILTER_VALUE)}
            onStatusClick={setStatusFilter}
            onOverdueClick={() => setStatusFilter(OVERDUE_FILTER_VALUE)}
            backgroundColor={chartBackgroundColor}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          {!project ? (
            <MyTasksBreakdownChart
              items={projectOptions}
              otherLabel="Other Projects"
              title="By Project"
              onValueClick={(values) => setFilterValues(projectFilters, setProjectFilters, values)}
              backgroundColor={chartBackgroundColor}
            />
          ) : (
            <MyTasksBreakdownChart
              items={docTypeOptions}
              otherLabel="Other Document Types"
              title="By Type"
              onValueClick={(values) => setFilterValues(docTypeFilters, setDocTypeFilters, values)}
              backgroundColor={chartBackgroundColor}
            />
          )}
        </Grid>
      </Grid>
      <div
        style={{
          display: 'flex',
          marginTop: 8,
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap-reverse',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex' }}>
          {tabs.map((tab) => (
            <Button
              key={tab.value}
              onClick={() => setUpcomingType(tab.value)}
              style={{ borderBottom: upcomingType === tab.value ? '2px solid #0947B9' : 'none' }}
            >
              {tab.label}
            </Button>
          ))}
        </div>
        <div style={{ display: 'inline-flex', flexGrow: 100 }} />
        <Button
          ref={filterButtonRef}
          color="primary"
          variant="contained"
          style={{ marginRight: 8 }}
          onClick={() => setFilterDialogOpen(true)}
        >
          {activeFilterCount === 0 ? (
            <>
              <Add style={{ paddingRight: 2 }} />
              FILTER
            </>
          ) : activeFilterCount === 1 ? (
            '1 FILTER SELECTED'
          ) : (
            `${activeFilterCount} FILTERS SELECTED`
          )}
        </Button>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_event, value: TaskView | null) => {
            if (value) setView(value);
          }}
        >
          <ToggleButton value="table" aria-label="table view">
            <ViewListIcon />
          </ToggleButton>
          <ToggleButton value="calendar" aria-label="calendar view">
            <CalendarTodayOutlinedIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      <MyTasksFilterDialog
        dialogOpen={filterDialogOpen}
        handleClose={() => setFilterDialogOpen(false)}
        anchor={filterButtonRef.current}
        statusOptions={statusOptions}
        overdueOption={overdueOption}
        projectOptions={projectOptions}
        showProjectFilter={!project}
        docTypeOptions={docTypeOptions}
        statusFilters={statusFilters}
        projectFilters={projectFilters}
        docTypeFilters={docTypeFilters}
        onApply={(status, projectVals, docTypeVals) => {
          setStatusFilters(status);
          setProjectFilters(projectVals);
          setDocTypeFilters(docTypeVals);
        }}
      />
      {view === 'table' && (
        <DashboardUpcomingList
          rowsPerPageOptions={rowsPerPageOptions}
          soonestDueDocuments={filteredDocuments}
          upcomingType={upcomingType}
          loading={loading}
          projectColors={projectColorsById}
          docTypeColors={docTypeColorsById}
        />
      )}
      {view === 'calendar' && (
        <DashboardUpcomingCalendar
          soonestDueDocuments={filteredDocuments}
          loading={loading}
          calendarRef={calendarRef}
          iCalendar={iCalendar}
        />
      )}
    </>
  );
}
